'use client';

import { useCallback } from 'react';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { ValueHelpers } from '@headless-workspace/utils';
import { isProductSet, ProductDetailVariantValue, useProductDetailContext } from '../../..';

export const useProductSet = () => {
    const {
        selectedProductSetVariants,
        setSelectedProductSetVariants,
        productSetList,
        updateProductDetails,
        productDetails,
        getProductSetInfo,
    } = useProductDetailContext();

    const getSelectedProductSetVariant = useCallback(
        (index: number) => {
            return selectedProductSetVariants?.[index];
        },
        [selectedProductSetVariants],
    );

    // compute the price of all the selected variants of all the products in the set
    const getProductSetPrice = useCallback((variants?: Record<number, ProductDetailVariantValue | undefined>) => {
        const productSetListValues = ValueHelpers.mapObjectValues(variants);

        // if one price is falsy ( 0 or undefined ), return undefined
        if (productSetListValues.some((variant) => !variant?.price)) {
            return undefined;
        }

        return productSetListValues.reduce((acc, variant) => {
            return acc + (variant?.price ?? 0);
        }, 0);
    }, []);

    const getVariant = useCallback(
        (index: number, variantId: string) => {
            return productSetList?.[index]?.variants.find((variant) => variant.id === variantId);
        },
        [productSetList],
    );

    // used in variant selector to set the selected variant
    const setProductSetVariant = useCallback(
        (index: number) => async (variantId: string) => {
            if (!isProductSet(productDetails)) {
                return;
            }

            const variant = getVariant(index, variantId);

            const newVariants = { ...selectedProductSetVariants, [index]: variant };

            const loyaltyPointsResult = await getProductSetInfo({
                productId: productDetails.productSetId,
                variantIds: [
                    ...Object.values(newVariants).reduce((acc, variant) => {
                        return variant?.id ? [...acc, variant.id] : acc;
                    }, [] as string[]),
                ],
            });

            if (variant) {
                setSelectedProductSetVariants(() => {
                    const newPrice = getProductSetPrice(newVariants);
                    updateProductDetails({
                        price: newPrice, // Update the price in the product variant details
                        loyaltyBenefits: {
                            earnedPoints: unwrapResultSuccess(loyaltyPointsResult)?.loyaltyPoints,
                        },
                    });
                    return newVariants;
                });
            }
        },
        [
            productDetails,
            getVariant,
            selectedProductSetVariants,
            getProductSetInfo,
            setSelectedProductSetVariants,
            getProductSetPrice,
            updateProductDetails,
        ],
    );

    // used in variant selector hover to show the image of the variant
    const setProductSetVariantPreview = useCallback(
        (index: number) => (variantId?: string) => {
            // reset the variant ( image ) if the variantId is not provided
            if (!variantId) {
                const previewVariantId = getSelectedProductSetVariant(index)?.id;
                if (!previewVariantId) {
                    return;
                }

                const variant = getVariant(index, previewVariantId);
                if (variant) {
                    setSelectedProductSetVariants((prevState) => ({
                        ...prevState,
                        [index]: variant,
                    }));
                }
            } else {
                // change the variant image
                const variant = getVariant(index, variantId);
                if (variant) {
                    setSelectedProductSetVariants((prevState) => {
                        const selectedVariant = prevState[index];
                        const preview = selectedVariant
                            ? { ...selectedVariant, image: variant.image }
                            : selectedVariant;
                        return {
                            ...prevState,
                            [index]: preview,
                        };
                    });
                }
            }
        },
        [getVariant, getSelectedProductSetVariant, setSelectedProductSetVariants],
    );

    return {
        selectedProductSetVariants,
        setSelectedProductSetVariants,
        getSelectedProductSetVariant,
        setProductSetVariant,
        setProductSetVariantPreview,
    };
};
